import React from 'react'
import { MessageCircle, Mic, Shield, Globe, HelpCircle } from 'lucide-react'

export const UserGuidePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-xl">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">CrossTalk User Guide</h1>
              <p className="text-sm text-gray-600">Travel Communication Made Easy</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm p-8">
          
          {/* Introduction */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4">
              🌍 Break language barriers while traveling
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What is CrossTalk?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              CrossTalk helps travelers communicate with locals in any language through real-time voice translation, 
              smart flashcard interface, travel safety alerts, and cultural context.
            </p>
          </div>

          {/* Quick Start */}
          <section className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              🚀 Quick Start Guide
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-blue-50 rounded-xl p-6">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-white font-bold text-lg">1</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Start a New Conversation</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Click "Start New Session"</li>
                  <li>• Choose your language</li>
                  <li>• Choose local language</li>
                  <li>• Click "Create Session"</li>
                </ul>
              </div>
              
              <div className="bg-green-50 rounded-xl p-6">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-white font-bold text-lg">2</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Share with Local Person</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Show QR code on screen</li>
                  <li>• Or share session link</li>
                  <li>• They join instantly</li>
                  <li>• No setup needed!</li>
                </ul>
              </div>
              
              <div className="bg-purple-50 rounded-xl p-6">
                <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-white font-bold text-lg">3</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Start Communicating</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• 🎙️ Tap microphone (recommended)</li>
                  <li>• Or type your message</li>
                  <li>• Get instant translations</li>
                  <li>• See flashcard interface</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Key Features */}
          <section className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">💬 Key Features</h3>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mic className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Voice Communication</h4>
                    <p className="text-gray-600 text-sm">
                      Tap the large blue microphone button, speak clearly, and get instant translations. 
                      More accurate than typing!
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Flashcard Interface</h4>
                    <p className="text-gray-600 text-sm">
                      Messages appear as flashcards. Scroll vertically through messages; tap a card to flip and see
                      cultural context and original text.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Safety Alerts</h4>
                    <p className="text-gray-600 text-sm">
                      AI-powered scam detection warns you about fake police, overcharging, pressure tactics, 
                      and fake emergencies.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Cultural Context</h4>
                    <p className="text-gray-600 text-sm">
                      Learn about local customs, etiquette, and cultural nuances. Understand what people 
                      really mean beyond just words.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Travel Scenarios */}
          <section className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">📱 Common Travel Scenarios</h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-orange-50 rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">🍽️</div>
                <h4 className="font-semibold text-gray-900 mb-1">Ordering Food</h4>
                <p className="text-xs text-gray-600">Ask about ingredients, specialties, dining etiquette</p>
              </div>
              
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">🗺️</div>
                <h4 className="font-semibold text-gray-900 mb-1">Getting Directions</h4>
                <p className="text-xs text-gray-600">Navigate to tourist sites, understand transportation</p>
              </div>
              
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">🛍️</div>
                <h4 className="font-semibold text-gray-900 mb-1">Shopping</h4>
                <p className="text-xs text-gray-600">Negotiate prices, understand local shopping customs</p>
              </div>
              
              <div className="bg-red-50 rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">🚨</div>
                <h4 className="font-semibold text-gray-900 mb-1">Emergencies</h4>
                <p className="text-xs text-gray-600">Communicate urgent needs, get help from locals</p>
              </div>
            </div>
          </section>

          {/* Pro Tips */}
          <section className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">💡 Pro Tips for Travelers</h3>
            
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    🎙️ Voice Communication
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Speak clearly at normal pace</li>
                    <li>• Use microphone - more accurate than typing</li>
                    <li>• Pause between sentences</li>
                    <li>• Use short phrases for clarity</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    🛡️ Staying Safe
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Pay attention to safety alerts</li>
                    <li>• Trust your instincts</li>
                    <li>• Don't ignore money request warnings</li>
                    <li>• Ask locals for safety advice</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <HelpCircle className="w-6 h-6" />
              Frequently Asked Questions
            </h3>
            
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Do I need to download anything?</h4>
                <p className="text-gray-600 text-sm">No! CrossTalk works in your web browser. Just visit the website.</p>
              </div>
              
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Does the other person need to create an account?</h4>
                <p className="text-gray-600 text-sm">No! They just scan your QR code or click your link to join instantly.</p>
              </div>
              
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-2">What if I get a safety alert?</h4>
                <p className="text-gray-600 text-sm">Take it seriously! Read the suggestions and trust your instincts about the situation.</p>
              </div>
              
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Is it free to use?</h4>
                <p className="text-gray-600 text-sm">Yes! CrossTalk is free for travelers.</p>
              </div>
            </div>
          </section>

          {/* Call to Action */}
          <div className="text-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Ready to Start Your Journey? 🌍</h3>
            <p className="mb-6 opacity-90">
              CrossTalk is your cultural bridge and safety companion while traveling.
            </p>
            <button
              onClick={() => window.close()}
              className="bg-white text-blue-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
            >
              Start Using CrossTalk
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
